export type InvoicePdfData = {
  invoice_number: string;
  tenant_name_snapshot: string;
  currency: string;
  issue_date: string;
  due_date: string;
  billing_period_start?: string | null;
  billing_period_end?: string | null;
  line_items: Array<{ description: string; quantity: string; unit_price: number; line_total: number }>;
  total: number;
  notes?: string | null;
};

const ascii = (value: string) => value.normalize("NFKD").replace(/[^\x20-\x7E]/g, "");
const esc = (value: string) => ascii(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
const money = (value: number, currency: string) => `${currency} ${value.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const trim = (value: string, max: number) => ascii(value).length > max ? ascii(value).slice(0, max - 3) + "..." : ascii(value);

export function buildInvoicePdf(data: InvoicePdfData): Blob {
  const commands: string[] = [
    "0.055 0.165 0.29 rg", "0 690 612 102 re f", "1 1 1 rg",
    "BT /F2 24 Tf 48 744 Td (RemediKeep by RMBCS) Tj ET",
    "BT /F1 10 Tf 48 724 Td (Client Services Invoice) Tj ET",
    "0.055 0.165 0.29 rg",
    `BT /F2 18 Tf 48 650 Td (INVOICE ${esc(data.invoice_number)}) Tj ET`,
    `BT /F2 12 Tf 48 620 Td (Bill to: ${esc(data.tenant_name_snapshot)}) Tj ET`,
    `BT /F1 10 Tf 48 600 Td (Issue date: ${esc(data.issue_date)}) Tj ET`,
    `BT /F1 10 Tf 48 584 Td (Due date: ${esc(data.due_date)}) Tj ET`,
  ];
  if (data.billing_period_start && data.billing_period_end) {
    commands.push(`BT /F1 10 Tf 320 600 Td (Billing period: ${esc(data.billing_period_start)} to ${esc(data.billing_period_end)}) Tj ET`);
  }
  commands.push(
    "0.92 0.95 0.98 rg", "44 522 524 34 re f", "0.055 0.165 0.29 rg",
    "BT /F2 9 Tf 52 535 Td (DESCRIPTION) Tj ET",
    "BT /F2 9 Tf 352 535 Td (QTY) Tj ET",
    "BT /F2 9 Tf 410 535 Td (RATE) Tj ET",
    "BT /F2 9 Tf 498 535 Td (AMOUNT) Tj ET",
  );
  data.line_items.slice(0, 12).forEach((item, index) => {
    const y = 504 - index * 26;
    commands.push(
      `BT /F1 9 Tf 52 ${y} Td (${esc(trim(item.description, 52))}) Tj ET`,
      `BT /F1 9 Tf 352 ${y} Td (${esc(String(item.quantity))}) Tj ET`,
      `BT /F1 9 Tf 410 ${y} Td (${esc(item.unit_price.toFixed(2))}) Tj ET`,
      `BT /F1 9 Tf 498 ${y} Td (${esc(item.line_total.toFixed(2))}) Tj ET`,
      `0.88 0.9 0.92 RG 48 ${y - 9} m 564 ${y - 9} l S`,
    );
  });
  const totalY = Math.max(150, 490 - Math.min(data.line_items.length, 12) * 26);
  commands.push(
    "0.055 0.165 0.29 rg",
    `BT /F2 14 Tf 380 ${totalY} Td (TOTAL) Tj ET`,
    `BT /F2 14 Tf 464 ${totalY} Td (${esc(money(data.total, data.currency))}) Tj ET`,
  );
  if (data.notes) {
    commands.push(
      `BT /F2 9 Tf 48 ${totalY - 42} Td (NOTES) Tj ET`,
      `BT /F1 8 Tf 48 ${totalY - 58} Td (${esc(trim(data.notes, 105))}) Tj ET`,
    );
  }
  commands.push(
    "0.55 0.61 0.67 rg",
    "BT /F1 8 Tf 48 66 Td (Questions: management@rmbcservices.com) Tj ET",
    "BT /F1 8 Tf 48 50 Td (Generated by RemediKeep - payment status is tracked separately in the Platform Control Center.) Tj ET",
  );
  const stream = commands.join("\n");
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> /Contents 4 0 R >>",
    `<< /Length ${new TextEncoder().encode(stream).length} >>\nstream\n${stream}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
  ];
  let pdf = "%PDF-1.4\n%RemediKeep Invoice\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(new TextEncoder().encode(pdf).length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => { pdf += `${String(offset).padStart(10, "0")} 00000 n \n`; });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  return new Blob([pdf], { type: "application/pdf" });
}
